import { useState, type ReactNode } from 'react';
import { useInfiniteQuery, useQuery } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { ApiError } from '@/api/client';
import { billingApi } from '@/api/ops';
import type { MoneySummary, MoneyTestFilter, Payment, PaymentListFilter } from '@/api/opsTypes';
import { PERMISSIONS } from '@/api/types';
import { BarList } from '@/components/BarList';
import { CompareBars } from '@/components/CompareBars';
import { Donut } from '@/components/Donut';
import { Select, Segmented } from '@/components/Select';
import { TrendChart } from '@/components/TrendChart';
import { EstimateChip, StatusChip } from '@/components/ops/Badges';
import { TextField } from '@/components/ops/Forms';
import { KeyValues, LoadMore, PageHeader, Section } from '@/components/ops/Layout';
import { EnvironmentPicker } from '@/components/ops/Pickers';
import { RequirePermission } from '@/components/ops/PermissionGate';
import { ProblemCallout, QueryState, UnavailablePanel } from '@/components/ops/Problem';
import { TenantName } from '@/components/ops/TenantPicker';
import { displayDecimals, formatCredits, isValidScale, microToDisplayed } from '@/ops/credits';
import { useCurrencyDigits } from '@/ops/currencies';
import { downloadCsv } from '@/ops/csv';
import { formatDay, formatMinor, formatNumber, formatPeriod, formatTimestamp, minorDigits, shortId } from '@/ops/format';
import { LIVE } from '@/ops/live';
import {
  EXPORT_CAP, PERIOD_PRESETS, collectPages, customRangeProblem, dayCount, deltaText, granularityFor, minorToMajor,
  paymentsCsv, presetRange, previousRange, rangeInstants, testModeFilter, walletLabel,
  type DayRange, type PeriodPreset,
} from '@/ops/money';
import { useCreditScale } from '@/ops/useCreditScale';
import { useSelectedEnvironment } from '@/ops/useEnvironment';

const bk = (env: string, ...rest: (string | number | boolean | undefined)[]) => ['ops', 'billing', env, ...rest] as const;

const TEST_OPTIONS: { value: MoneyTestFilter; label: string }[] = [
  { value: 'include', label: 'Include' },
  { value: 'exclude', label: 'Exclude' },
  { value: 'only', label: 'Only' },
];

/** Status colours carry the payment's state (reserved hues); everything else is the neutral. */
const STATUS_FILL: Record<string, string> = {
  succeeded: 'var(--viz-good)',
  paid: 'var(--viz-good)',
  failed: 'var(--viz-critical)',
  pending: 'var(--viz-warning)',
  created: 'var(--viz-warning)',
};

/**
 * Money (2026-10-14): every pound Anyware collects, for one environment and window — what came in, what it was for
 * (credit sales, the service fee, subscriptions, VAT), what went back, the credits it bought, and what is still owed or
 * still unspent. One aggregate from billing (`money/summary`) plus billing's payment list; nothing is summed or cached
 * here (CP2). The summary and the payment list fail separately: billing down shows its labelled panel where those figures
 * would be, never a zero.
 */
export function MoneyPage() {
  const { env, setEnv, environments } = useSelectedEnvironment();
  const [preset, setPreset] = useState<PeriodPreset>('30d');
  const [customFrom, setCustomFrom] = useState('');
  const [customTo, setCustomTo] = useState('');
  const [currency, setCurrency] = useState('');
  const [test, setTest] = useState<MoneyTestFilter>('include');
  useCurrencyDigits(env);

  const range = presetRange(preset, new Date(), { from: customFrom, to: customTo });
  const customProblem = preset === 'custom' ? customRangeProblem(customFrom, customTo) : null;

  return (
    <RequirePermission permission={PERMISSIONS.costsRead} what="Money">
      <PageHeader
        title="Money"
        subtitle="What we collected, what it was for, what went back — live from billing, by payment date (UTC days)."
        actions={<EnvironmentPicker env={env} environments={environments} onChange={setEnv} />}
      />
      {env && (
        <MoneyFilters env={env} preset={preset} onPreset={setPreset} customFrom={customFrom} customTo={customTo}
          onCustomFrom={setCustomFrom} onCustomTo={setCustomTo} customProblem={customProblem}
          currency={currency} onCurrency={setCurrency} test={test} onTest={setTest} />
      )}
      {env && range && <MoneyDashboard env={env} range={range} currency={currency} test={test} />}
    </RequirePermission>
  );
}

function MoneyFilters(props: {
  env: string;
  preset: PeriodPreset;
  onPreset: (p: PeriodPreset) => void;
  customFrom: string;
  customTo: string;
  onCustomFrom: (v: string) => void;
  onCustomTo: (v: string) => void;
  customProblem: string | null;
  currency: string;
  onCurrency: (v: string) => void;
  test: MoneyTestFilter;
  onTest: (v: MoneyTestFilter) => void;
}) {
  // Reference data (not a money figure): the currencies billing knows, and its default for the placeholder.
  const currencies = useQuery({
    queryKey: bk(props.env, 'currencies'),
    queryFn: () => billingApi.currencies(props.env),
    staleTime: Infinity,
    gcTime: Infinity,
    retry: false,
  });
  const settings = useQuery({ queryKey: bk(props.env, 'settings'), queryFn: () => billingApi.settings(props.env), ...LIVE });
  const defaultCurrency = settings.data?.default_currency;

  return (
    <div className="filterbar ops-filters money-filters">
      <Select label="Period" inline searchable={false} value={props.preset} onChange={(v) => props.onPreset(v as PeriodPreset)}
        options={PERIOD_PRESETS.map((p) => ({ value: p.value, label: p.label }))} />
      {props.preset === 'custom' && (
        <>
          <TextField label="From" type="date" value={props.customFrom} onChange={props.onCustomFrom} />
          <TextField label="To" type="date" value={props.customTo} onChange={props.onCustomTo} error={props.customProblem} />
        </>
      )}
      <Select label="Currency" inline searchable={false} value={props.currency} onChange={props.onCurrency}
        placeholder={defaultCurrency ? `Billing default (${defaultCurrency})` : 'Billing default'}
        options={(currencies.data?.currencies ?? []).map((c) => ({ value: c.code, label: c.code }))} />
      <Segmented<MoneyTestFilter> label="Test payments" inline value={props.test} onChange={props.onTest} options={TEST_OPTIONS} />
    </div>
  );
}

function MoneyDashboard({ env, range, currency, test }: { env: string; range: DayRange; currency: string; test: MoneyTestFilter }) {
  const granularity = granularityFor(range);
  const instants = rangeInstants(range);
  const previous = previousRange(range);
  const previousInstants = rangeInstants(previous);
  const days = dayCount(range);

  const summary = useQuery({
    queryKey: bk(env, 'money-summary', instants.from, instants.to, granularity, currency, test),
    queryFn: () => billingApi.moneySummary(env, { ...instants, granularity, currency: currency || undefined, test }),
    ...LIVE,
  });
  // The previous equal-length window, for the two headline deltas only. Its failure removes the delta, nothing else.
  const before = useQuery({
    queryKey: bk(env, 'money-summary', previousInstants.from, previousInstants.to, granularityFor(previous), currency, test),
    queryFn: () => billingApi.moneySummary(env, { ...previousInstants, granularity: granularityFor(previous), currency: currency || undefined, test }),
    ...LIVE,
  });
  const scaleState = useCreditScale(env);

  return (
    <div className="stack">
      <AttentionStrip env={env} />
      <QueryState query={summary} service="billing">
        {(data) => (
          <SummaryPanels env={env} data={data} test={test} days={days}
            previous={before.isError ? null : before.data ?? null}
            scale={isValidScale(data.credit_display_scale) ? data.credit_display_scale : scaleState.scale} />
        )}
      </QueryState>
      <LatestPayments env={env} instants={instants} test={test} />
    </div>
  );
}

// ─── Attention strip ──────────────────────────────────────────────────────

function AttentionStrip({ env }: { env: string }) {
  const parked = useQuery({ queryKey: bk(env, 'parked-events', 'count'), queryFn: () => billingApi.parkedEvents(env, { limit: 50 }), ...LIVE });
  const refunds = useQuery({ queryKey: bk(env, 'needs-refund'), queryFn: () => billingApi.paymentsNeedingRefund(env), ...LIVE });
  const shortfalls = useQuery({ queryKey: bk(env, 'clawback-shortfalls'), queryFn: () => billingApi.clawbackShortfalls(env), ...LIVE });

  const item = (label: string, tab: string, query: { isPending: boolean; isError: boolean }, count: number | undefined, capped = false) => (
    <Link key={tab} to={`/ops/money-operations?tab=${tab}`} className="card stat-tile tile-link money-attention-item">
      <div className="stat-label t-label">{label}</div>
      <div className="t-heading tabular" style={{ color: count ? 'var(--viz-critical)' : undefined }}>
        {query.isError ? <span className="note-error t-body">could not be read</span>
          : query.isPending || count === undefined ? '…'
            : `${count.toLocaleString('en-US')}${capped ? '+' : ''}`}
      </div>
    </Link>
  );

  return (
    <section className="money-attention" aria-label="Needs a person">
      {item('Parked ledger events', 'parked', parked, parked.data?.events.length, (parked.data?.events.length ?? 0) >= 50)}
      {item('Payments to refund by hand', 'refunds', refunds, refunds.data?.payments.length)}
      {item('Clawback shortfalls', 'shortfalls', shortfalls, shortfalls.data?.shortfalls.length)}
    </section>
  );
}

// ─── Summary panels ───────────────────────────────────────────────────────

function SummaryPanels({
  env, data, test, days, previous, scale,
}: {
  env: string;
  data: MoneySummary;
  test: MoneyTestFilter;
  days: number;
  previous: MoneySummary | null;
  scale: number | null;
}) {
  const t = data.totals;
  const cur = data.currency;
  const money = (minor: number | null | undefined) => formatMinor(minor, cur);
  const hasTest = test === 'only' || data.by_gateway.some((g) => g.test_mode && g.count > 0);
  const testChip = hasTest ? <StatusChip status={test === 'only' ? 'test payments only' : 'includes test payments'} tone="warning" /> : null;

  const unused = data.liability.reduce((sum, l) => sum + l.unused_credits, 0);
  const valued = data.liability.filter((l) => typeof l.unused_value_minor === 'number');
  const unusedValue = valued.reduce((sum, l) => sum + (l.unused_value_minor ?? 0), 0);
  // A wallet with no unit price is NOT valued at zero — the sub-line says which part the figure covers.
  const unusedValueText = valued.length === 0
    ? 'all time · not valued (no unit price)'
    : valued.length === data.liability.length
      ? `≈ ${money(unusedValue)} at today's price · all time`
      : `≈ ${money(unusedValue)} for ${valued.map((l) => walletLabel(l.wallet)).join(', ')} at today's price · the rest not valued · all time`;

  return (
    <>
      <div className="money-context t-caption muted">
        <span>{formatDay(data.from)} – {formatDay(new Date(Date.parse(data.to) - 1).toISOString())} · {cur} · by {data.granularity}</span>
        <span>generated {formatTimestamp(data.generated_at)}</span>
        {testChip}
      </div>

      <div className="kpi-row money-kpi">
        <Tile label="Gross collected" value={money(t.gross_collected_minor)}
          sub={deltaText(t.gross_collected_minor, previous?.totals.gross_collected_minor, days) ?? 'everything paid, tax included'} />
        <Tile label="Credit sales" value={money(t.credit_sales_minor)} sub="credits, after discounts" />
        <Tile label="Service fees" value={money(t.service_fees_minor)} sub="the fee on credit purchases" />
        <Tile label="VAT collected" value={money(t.vat_minor)} sub="owed to the tax authority" />
        <Tile label="Refunds" value={money(t.refunds_minor)} sub="credit notes issued" tone={t.refunds_minor > 0 ? 'warning' : undefined} />
        <Tile label="Net after refunds" value={money(t.net_after_refunds_minor)} sub="gross collected − refunds" />
        <Tile label="Purchases" value={formatNumber(t.purchases_count)}
          sub={[
            t.purchases_count > 0 ? `average ${money(t.average_purchase_minor)}` : null,
            deltaText(t.purchases_count, previous?.totals.purchases_count, days),
            `${formatNumber(t.paying_tenants)} paying tenant${t.paying_tenants === 1 ? '' : 's'}`,
          ].filter(Boolean).join(' · ')} />
        <Tile label="Credits sold" value={formatCredits(t.credits_sold, scale)}
          sub={`+ ${formatCredits(t.credits_granted_free, scale)} granted free`} />
        <Tile label={<>Unused credits <EstimateChip title="All time: every grant minus every use, per wallet. Valued at today's unit price." /></>}
          value={formatCredits(unused, scale)}
          sub={unusedValueText} />
        <Tile label="Failed / pending payments" value={`${formatNumber(t.failed_payments)} / ${formatNumber(t.pending_payments)}`}
          sub="in the period" tone={t.failed_payments > 0 ? 'critical' : undefined} />
      </div>

      <CollectedOverTime data={data} />

      <div className="grid-2">
        <Breakdown data={data} />
        <CreditsByWallet data={data} scale={scale} />
      </div>
      <div className="grid-2">
        <PaymentsByStatus data={data} />
        <ByGateway data={data} />
      </div>
      <div className="grid-2">
        <TopTenants env={env} data={data} />
        <div className="stack">
          <Section title="Outstanding invoices" meta="issued, not yet paid — now">
            <KeyValues rows={[
              { label: 'Issued, unpaid', value: `${formatNumber(data.outstanding_invoices.issued_count)} · ${money(data.outstanding_invoices.issued_minor)}` },
              {
                label: 'Overdue',
                value: <span className={data.outstanding_invoices.overdue_count > 0 ? 'note-error' : undefined}>
                  {formatNumber(data.outstanding_invoices.overdue_count)} · {money(data.outstanding_invoices.overdue_minor)}
                </span>,
              },
            ]} />
          </Section>
          <Liability data={data} scale={scale} />
        </div>
      </div>
    </>
  );
}

function Tile({ label, value, sub, tone }: { label: ReactNode; value: string; sub?: string; tone?: 'warning' | 'critical' }) {
  const color = tone === 'critical' ? 'var(--viz-critical)' : undefined;
  return (
    <div className="card stat-tile">
      <div className="stat-label t-label">{label}</div>
      <div className="stat-value tabular" style={{ color }}>{value}</div>
      {sub && <div className="stat-sub t-caption">{sub}</div>}
    </div>
  );
}

function CollectedOverTime({ data }: { data: MoneySummary }) {
  const digits = minorDigits(data.currency);
  const formatX = data.granularity === 'month' ? formatPeriod : undefined;
  const label = (period: string) => (data.granularity === 'month' ? formatPeriod(period) : formatDay(period));

  return (
    <Section title="Collected over time" meta={`${data.currency}, per ${data.granularity}`}>
      {data.series.length === 0 ? (
        <div className="empty-state t-body">No periods in this window.</div>
      ) : (
        <TrendChart
          height={240}
          formatX={formatX}
          formatValue={(v) => formatNumber(v, digits)}
          series={[
            { key: 'gross', label: 'Collected', color: 'var(--series-1)', points: data.series.map((p) => ({ date: p.period, value: minorToMajor(p.gross_collected_minor, digits) })) },
            { key: 'refunds', label: 'Refunds', color: 'var(--series-2)', points: data.series.map((p) => ({ date: p.period, value: minorToMajor(p.refunds_minor, digits) })) },
          ]}
        />
      )}
      <details className="viz-table">
        <summary>Table</summary>
        <div className="table-wrap">
          <table className="table">
            <thead><tr><th>Period</th><th className="num">Collected</th><th className="num">Credit sales</th><th className="num">Service fees</th><th className="num">VAT</th><th className="num">Refunds</th><th className="num">Purchases</th></tr></thead>
            <tbody>
              {data.series.map((p) => (
                <tr key={p.period}>
                  <td>{label(p.period)}</td>
                  <td className="num">{formatMinor(p.gross_collected_minor, data.currency)}</td>
                  <td className="num">{formatMinor(p.credit_sales_minor, data.currency)}</td>
                  <td className="num">{formatMinor(p.service_fees_minor, data.currency)}</td>
                  <td className="num">{formatMinor(p.vat_minor, data.currency)}</td>
                  <td className="num">{formatMinor(p.refunds_minor, data.currency)}</td>
                  <td className="num">{formatNumber(p.purchases_count)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </details>
    </Section>
  );
}

function Breakdown({ data }: { data: MoneySummary }) {
  const t = data.totals;
  const parts = [
    { label: 'Credit sales', value: t.credit_sales_minor },
    { label: 'Service fees', value: t.service_fees_minor },
    { label: 'Subscriptions', value: t.subscription_minor },
    { label: 'VAT', value: t.vat_minor },
    ...(t.other_minor !== 0 ? [{ label: 'Other', value: t.other_minor }] : []),
  ];
  const total = parts.reduce((sum, p) => sum + p.value, 0);

  return (
    <Section title="Where the money comes from" meta="share of gross collected">
      {total === 0 ? (
        <div className="empty-state t-body">Nothing collected in this window.</div>
      ) : (
        <CompareBars items={parts} valueLabel="Collected" formatValue={(v) => formatMinor(v, data.currency)} />
      )}
    </Section>
  );
}

function CreditsByWallet({ data, scale }: { data: MoneySummary; scale: number | null }) {
  const known = isValidScale(scale);
  const shown = (micro: number) => (known ? microToDisplayed(micro, scale)! : micro);
  return (
    <Section title="Credits sold by wallet" meta={known ? 'displayed credits' : 'micro-credits (display scale unknown)'}>
      {data.by_wallet.length === 0 ? (
        <div className="empty-state t-body">No credits sold or granted in this window.</div>
      ) : (
        <>
          <CompareBars
            items={data.by_wallet.map((w) => ({ label: walletLabel(w.wallet), value: shown(w.credits_sold), secondary: shown(w.credits_granted_free) }))}
            valueLabel="Sold" secondaryLabel="Granted free" showShare={false}
            formatValue={(v) => formatNumber(v, known ? displayDecimals(scale) : 0)}
          />
          <details className="viz-table">
            <summary>Table</summary>
            <table className="table">
              <thead><tr><th>Wallet</th><th className="num">Sold</th><th className="num">Granted free</th><th className="num">Sales</th><th className="num">Purchases</th></tr></thead>
              <tbody>
                {data.by_wallet.map((w) => (
                  <tr key={w.wallet}>
                    <td>{walletLabel(w.wallet)}</td>
                    <td className="num">{formatCredits(w.credits_sold, scale)}</td>
                    <td className="num">{formatCredits(w.credits_granted_free, scale)}</td>
                    <td className="num">{formatMinor(w.sales_minor, data.currency)}</td>
                    <td className="num">{formatNumber(w.purchases_count)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </details>
        </>
      )}
    </Section>
  );
}

function PaymentsByStatus({ data }: { data: MoneySummary }) {
  const rows = [...data.by_status].sort((a, b) => b.count - a.count);
  return (
    <Section title="Payments by status" meta="created in the window">
      {rows.length === 0 ? (
        <div className="empty-state t-body">No payments in this window.</div>
      ) : (
        <>
          <Donut
            slices={rows.map((r) => ({ label: r.status.replace(/_/g, ' '), value: r.count, color: STATUS_FILL[r.status] ?? 'var(--viz-deemphasis)' }))}
            centerLabel="payments" valueLabel="Payments" size={180}
          />
          <div className="table-wrap">
            <table className="table">
              <thead><tr><th>Status</th><th className="num">Payments</th><th className="num">Amount</th></tr></thead>
              <tbody>
                {rows.map((r) => (
                  <tr key={r.status}>
                    <td><StatusChip status={r.status} /></td>
                    <td className="num">{formatNumber(r.count)}</td>
                    <td className="num">{formatMinor(r.amount_minor, data.currency)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}
    </Section>
  );
}

function ByGateway({ data }: { data: MoneySummary }) {
  return (
    <Section title="By gateway" meta="our own payment gateways" flush>
      {data.by_gateway.length === 0 ? (
        <div className="empty-state t-body">No gateway took a payment in this window.</div>
      ) : (
        <div className="table-wrap">
          <table className="table">
            <thead><tr><th>Gateway</th><th className="num">Payments</th><th className="num">Succeeded</th><th className="num">Failed</th></tr></thead>
            <tbody>
              {data.by_gateway.map((g) => (
                <tr key={`${g.gateway_row_id ?? g.gateway_key}-${g.test_mode}`}>
                  <td>
                    {g.display_name ?? g.gateway_key} {g.test_mode && <StatusChip status="test" tone="warning" title="A gateway row in test mode — no real money moved." />}
                    <div className="t-caption muted mono">{g.gateway_key}{g.gateway_row_id ? ` · ${shortId(g.gateway_row_id)}` : ''}</div>
                  </td>
                  <td className="num">{formatNumber(g.count)}</td>
                  <td className="num">{formatMinor(g.succeeded_minor, data.currency)}</td>
                  <td className="num">{g.failed_count > 0 ? <span className="note-error">{formatNumber(g.failed_count)}</span> : formatNumber(g.failed_count)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </Section>
  );
}

function tenantHref(env: string, id: string): string {
  return `/ops/tenants/${encodeURIComponent(env)}/${encodeURIComponent(id)}?tab=billing`;
}

function TopTenants({ env, data }: { env: string; data: MoneySummary }) {
  return (
    <Section title="Top paying tenants" meta="by gross collected">
      <BarList
        rows={data.top_tenants.map((tt) => ({ label: tt.name ?? shortId(tt.tenant_id), value: tt.gross_minor }))}
        limit={10}
        emptyLabel="No tenant paid in this window."
        formatValue={(v) => formatMinor(v, data.currency)}
      />
      {data.top_tenants.length > 0 && (
        <details className="viz-table">
          <summary>Table</summary>
          <table className="table">
            <thead><tr><th>Tenant</th><th className="num">Gross</th><th className="num">Purchases</th><th>Last paid</th></tr></thead>
            <tbody>
              {data.top_tenants.map((tt) => (
                <tr key={tt.tenant_id}>
                  <td>{tt.name ? <Link to={tenantHref(env, tt.tenant_id)}>{tt.name}</Link> : <TenantName env={env} id={tt.tenant_id} tab="billing" />}</td>
                  <td className="num">{formatMinor(tt.gross_minor, data.currency)}</td>
                  <td className="num">{formatNumber(tt.purchases_count)}</td>
                  <td className="t-caption">{formatTimestamp(tt.last_paid_at)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </details>
      )}
    </Section>
  );
}

function Liability({ data, scale }: { data: MoneySummary; scale: number | null }) {
  return (
    <Section title={<>Unused credits (liability) <EstimateChip title="Granted minus used, all time; the value is the unused credits at today's unit price." /></>}
      meta={isValidScale(scale) ? 'credits · all time, not the window' : 'micro-credits · all time, not the window'} flush>
      {data.liability.length === 0 ? (
        <div className="empty-state t-body">No credits granted yet.</div>
      ) : (
        <div className="table-wrap money-liability">
          <table className="table">
            <thead><tr><th>Wallet</th><th className="num">Granted</th><th className="num">Used</th><th className="num">Unused</th><th className="num">Value today</th></tr></thead>
            <tbody>
              {data.liability.map((l) => (
                <tr key={l.wallet}>
                  <td>{walletLabel(l.wallet)}</td>
                  <td className="num">{formatCredits(l.granted_credits, scale, { unit: false })}</td>
                  <td className="num">{formatCredits(l.consumed_credits, scale, { unit: false })}</td>
                  <td className="num"><strong>{formatCredits(l.unused_credits, scale, { unit: false })}</strong></td>
                  <td className="num">
                    {l.unused_value_minor === null || l.unused_value_minor === undefined
                      ? <span className="subtle" title="No credits.unit price for this wallet">—</span>
                      : <>≈ {formatMinor(l.unused_value_minor, data.currency)}</>}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </Section>
  );
}

// ─── Latest payments ──────────────────────────────────────────────────────

function LatestPayments({ env, instants, test }: { env: string; instants: { from: string; to: string }; test: MoneyTestFilter }) {
  const filter: PaymentListFilter = { from: instants.from, to: instants.to, test_mode: testModeFilter(test) };
  const payments = useInfiniteQuery({
    queryKey: bk(env, 'money-payments', instants.from, instants.to, test),
    queryFn: ({ pageParam }) => billingApi.payments(env, { ...filter, limit: 50, cursor: pageParam }),
    initialPageParam: null as string | null,
    getNextPageParam: (last) => last.next_cursor ?? undefined,
    ...LIVE,
  });
  const [exporting, setExporting] = useState<{ count: number } | null>(null);
  const [exportResult, setExportResult] = useState<{ count: number; truncated: boolean } | null>(null);
  const [exportError, setExportError] = useState<unknown>(null);
  const rows = payments.data?.pages.flatMap((p) => p.payments) ?? [];
  const fileName = (suffix: string) => `payments_${env}_${instants.from.slice(0, 10)}_${instants.to.slice(0, 10)}${suffix}.csv`;

  const exportAll = async () => {
    setExporting({ count: 0 });
    setExportError(null);
    setExportResult(null);
    try {
      const result = await collectPages<Payment>(
        async (cursor) => {
          const page = await billingApi.payments(env, { ...filter, limit: 100, cursor });
          return { rows: page.payments, next_cursor: page.next_cursor };
        },
        EXPORT_CAP,
        (count) => setExporting({ count }),
      );
      downloadCsv(fileName('_all'), paymentsCsv(result.rows));
      setExportResult({ count: result.rows.length, truncated: result.truncated });
    } catch (error) {
      setExportError(error);
    } finally {
      setExporting(null);
    }
  };

  return (
    <Section title="Latest payments" meta="created in the window, newest first" flush
      actions={
        <>
          <button type="button" className="btn btn-ghost btn-sm" disabled={rows.length === 0}
            onClick={() => downloadCsv(fileName(''), paymentsCsv(rows))}>Export CSV</button>
          <button type="button" className="btn btn-ghost btn-sm" disabled={exporting !== null || payments.isError}
            onClick={() => { void exportAll(); }}>
            {exporting ? `Exporting… ${exporting.count.toLocaleString('en-US')} rows` : 'Export all in period'}
          </button>
        </>
      }>
      {exportResult && (
        <div className="card-body" style={{ paddingBottom: 0 }}>
          <span className="t-caption muted" role="status">
            Exported {exportResult.count.toLocaleString('en-US')} payments
            {exportResult.truncated ? ` — stopped at ${EXPORT_CAP.toLocaleString('en-US')}; narrow the period for the rest.` : '.'}
          </span>
        </div>
      )}
      {exportError !== null && (
        <div className="card-body" style={{ paddingBottom: 0 }}>
          {exportError instanceof ApiError && exportError.isUnavailable
            ? <UnavailablePanel error={exportError} service="billing" />
            : <ProblemCallout error={exportError} title="The export stopped" />}
        </div>
      )}
      <QueryState query={{ ...payments, data: payments.data ? rows : undefined }} service="billing">
        {(list) => list.length === 0 ? (
          <div className="empty-state t-body">No payments in this window.</div>
        ) : (
          <div className="table-wrap">
            <table className="table">
              <thead><tr><th>Time</th><th>Tenant</th><th>Purpose</th><th className="num">Amount</th><th>Status</th><th>Gateway</th></tr></thead>
              <tbody>
                {list.map((p) => (
                  <tr key={p.id}>
                    <td className="t-caption">{formatTimestamp(p.created_at)}</td>
                    <td>{p.tenant_name ? <Link to={tenantHref(env, p.tenant_id)}>{p.tenant_name}</Link> : <TenantName env={env} id={p.tenant_id} tab="billing" />}</td>
                    <td className="t-caption">{p.purpose.replace(/_/g, ' ')}</td>
                    <td className="num">
                      {formatMinor(p.amount_minor, p.currency)}
                      {p.refunded_minor > 0 && <div className="t-caption muted">refunded {formatMinor(p.refunded_minor, p.currency)}</div>}
                    </td>
                    <td><StatusChip status={p.status} title={p.failure_reason ?? undefined} /></td>
                    <td className="t-caption">
                      <span className="mono">{p.gateway_key}</span>
                      {p.test_mode && <> <StatusChip status="test" tone="warning" title="Paid through a gateway in test mode — no real money moved." /></>}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </QueryState>
      <LoadMore hasMore={!!payments.hasNextPage} loading={payments.isFetchingNextPage} onLoad={() => payments.fetchNextPage()} />
    </Section>
  );
}
